import { useAthleteProfile } from "@/hooks/use-athlete-profile";
import { UnitsRender, SkeletonLoader } from "@/components/system";

/**
 * Body sub-area card: identity + currently-effective body fields, with auto-derived
 * BMI / LBM / FFMI / BMR / TDEE shown alongside the source fields.
 *
 * Mobile-first: 2-col on phones, 3-col on tablet, 6-col on desktop.
 * Derived numbers come from the DB (athlete_profile triggers in BW12 migration);
 * if the trigger hasn't fired (older rows), we fall back to client-side compute.
 */

type Profile = {
  display_name: string | null;
  date_of_birth: string | null;
  sex_at_birth: string | null;
  height_cm: number | null;
  current_weight_kg: number | null;
  current_body_fat_pct: number | null;
  goal_weight_kg: number | null;
  current_vo2max_running: number | null;
  current_ftp_w: number | null;
  current_lthr_bpm: number | null;
  bmi?: number | null;
  lean_body_mass_kg?: number | null;
  ffmi?: number | null;
  bmr_kcal?: number | null;
  tdee_kcal?: number | null;
};

export function AthleteProfileSummary() {
  const q = useAthleteProfile();

  if (q.isLoading) return <SkeletonLoader variant="card" />;
  if (q.isError || !q.data) {
    return (
      <p style={{ color: "var(--fg-3)", fontSize: "var(--type-body-s)" }}>
        Profile unavailable.
      </p>
    );
  }

  const p = q.data as Profile;
  const ageY = p.date_of_birth ? ageYears(p.date_of_birth) : null;
  const derived = computeDerived(p);

  return (
    <article
      className="haus-athlete-grid"
      style={{
        background: "var(--bg-2)",
        border: "1px solid var(--hair)",
        borderRadius: "var(--radius-card)",
        padding: "var(--space-5)",
        display: "grid",
        gap: "var(--space-4)",
      }}
    >
      <style>{`
        .haus-athlete-grid {
          grid-template-columns: repeat(2, 1fr);
        }
        @media (min-width: 640px) {
          .haus-athlete-grid { grid-template-columns: repeat(3, 1fr); }
        }
        @media (min-width: 1024px) {
          .haus-athlete-grid { grid-template-columns: repeat(6, 1fr); }
        }
      `}</style>

      <Stat label="Display" value={p.display_name ?? "—"} />
      <Stat label="Age · sex" value={[ageY ? `${ageY}y` : "—", p.sex_at_birth ?? "—"].join(" · ")} />
      <Stat label="Height" value={p.height_cm ? `${p.height_cm} cm` : "—"} />
      <Stat
        label="Weight"
        value={p.current_weight_kg ? <UnitsRender q={{ kind: "weight", kg: Number(p.current_weight_kg) }} /> : "—"}
        derived={derived.bmi != null ? `BMI ${derived.bmi.toFixed(1)} · ${bmiLabel(derived.bmi)}` : null}
        tone={derived.bmi != null ? bmiTone(derived.bmi) : null}
      />
      <Stat
        label="Goal weight"
        value={p.goal_weight_kg ? <UnitsRender q={{ kind: "weight", kg: Number(p.goal_weight_kg) }} /> : "—"}
        derived={
          p.current_weight_kg != null && p.goal_weight_kg != null
            ? `${(Number(p.goal_weight_kg) - Number(p.current_weight_kg)).toFixed(1)} kg to go`
            : null
        }
      />
      <Stat
        label="Body fat"
        value={p.current_body_fat_pct ? `${p.current_body_fat_pct}%` : "—"}
        derived={derived.lbm != null ? `LBM ${derived.lbm.toFixed(1)} kg` : null}
      />
      <Stat
        label="BMR"
        value={derived.bmr != null ? `${Math.round(derived.bmr)} kcal` : "—"}
        derived={derived.tdee != null ? `TDEE ${Math.round(derived.tdee)} kcal/day` : null}
      />
      <Stat
        label="FFMI"
        value={derived.ffmi != null ? derived.ffmi.toFixed(1) : "—"}
        derived={derived.ffmi != null ? ffmiLabel(derived.ffmi, p.sex_at_birth ?? null) : null}
      />
      <Stat
        label="VO₂max"
        value={p.current_vo2max_running ? `${p.current_vo2max_running}` : "—"}
        derived={p.current_vo2max_running ? "ml/kg/min" : null}
      />
      <Stat label="FTP" value={p.current_ftp_w ? `${p.current_ftp_w} W` : "—"} />
      <Stat label="LTHR" value={p.current_lthr_bpm ? `${p.current_lthr_bpm} bpm` : "—"} />
    </article>
  );
}

function Stat({
  label,
  value,
  derived,
  tone,
}: {
  label: string;
  value: React.ReactNode;
  derived?: string | null;
  tone?: "ok" | "warn" | "bad" | null;
}) {
  const toneColor =
    tone === "ok"
      ? "var(--severity-ok)"
      : tone === "warn"
        ? "var(--severity-warn)"
        : tone === "bad"
          ? "var(--severity-bad)"
          : "var(--fg-3)";
  return (
    <div>
      <div
        style={{
          fontSize: "var(--type-micro)",
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          color: "var(--fg-3)",
        }}
      >
        {label}
      </div>
      <div
        style={{
          marginTop: "var(--space-1)",
          color: "var(--fg)",
          fontSize: "var(--type-body)",
        }}
        className="mono"
      >
        {value}
      </div>
      {derived && (
        <div style={{ fontSize: 11, color: toneColor, fontStyle: "italic", marginTop: 2 }}>{derived}</div>
      )}
    </div>
  );
}

function ageYears(dob: string): number {
  const today = new Date();
  const birth = new Date(dob + "T00:00:00");
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
  return age;
}

function computeDerived(p: Profile): {
  bmi: number | null;
  lbm: number | null;
  ffmi: number | null;
  bmr: number | null;
  tdee: number | null;
} {
  const fromDb = {
    bmi: numOr(p.bmi),
    lbm: numOr(p.lean_body_mass_kg),
    ffmi: numOr(p.ffmi),
    bmr: numOr(p.bmr_kcal),
    tdee: numOr(p.tdee_kcal),
  };
  if (fromDb.bmi != null && fromDb.bmr != null) return fromDb;

  // Client fallback for rows that haven't run through the BW12 trigger yet.
  const w = numOr(p.current_weight_kg);
  const h = numOr(p.height_cm);
  if (!w || !h) return fromDb;
  const heightM = h / 100;
  const bmi = w / (heightM * heightM);
  const bf = numOr(p.current_body_fat_pct);
  const lbm = bf != null ? w * (1 - bf / 100) : null;
  const ffmi = lbm != null ? lbm / (heightM * heightM) : null;
  const dob = p.date_of_birth;
  let bmr: number | null = null;
  let tdee: number | null = null;
  if (dob) {
    const age = ageYears(dob);
    const sex = p.sex_at_birth ?? "male";
    bmr =
      sex === "female"
        ? 10 * w + 6.25 * h - 5 * age - 161
        : 10 * w + 6.25 * h - 5 * age + 5;
    tdee = bmr * 1.55;
  }
  return {
    bmi: fromDb.bmi ?? bmi,
    lbm: fromDb.lbm ?? lbm,
    ffmi: fromDb.ffmi ?? ffmi,
    bmr: fromDb.bmr ?? bmr,
    tdee: fromDb.tdee ?? tdee,
  };
}

function numOr(v: unknown): number | null {
  if (v == null) return null;
  const n = typeof v === "number" ? v : typeof v === "string" ? Number(v) : null;
  return n == null || Number.isNaN(n) ? null : n;
}

function bmiLabel(bmi: number): string {
  if (bmi < 18.5) return "underweight";
  if (bmi < 25) return "healthy";
  if (bmi < 30) return "overweight";
  return "obese";
}

function bmiTone(bmi: number): "ok" | "warn" | "bad" {
  if (bmi >= 18.5 && bmi < 25) return "ok";
  if (bmi >= 25 && bmi < 30) return "warn";
  return "bad";
}

function ffmiLabel(ffmi: number, sex: string | null): string {
  if (sex === "female") {
    if (ffmi < 16) return "low";
    if (ffmi < 19) return "average";
    return "high";
  }
  if (ffmi < 18) return "low";
  if (ffmi < 21) return "average";
  if (ffmi < 23) return "athletic";
  return "elite";
}
